import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from '../axios-default';

/**
 * localStorage key: stores a JSON object { notificationId, epoch }
 * for cross-tab login-session-scoped dismissal (cleared on explicit
 * logout or auth failure).
 */
const DISMISSAL_LS_KEY = 'afterLoginNotificationDismissed';
const HIDE_FUTURE_LS_KEY = 'afterLoginNotificationHidden';

/**
 * localStorage key: stores a unique login-epoch string that survives
 * across tabs within the same login session. A new epoch is generated
 * only on explicit login (isAuthenticated: false → true), NOT on page
 * refresh or new-tab opens (null → true).
 */
const EPOCH_LS_KEY = 'afterLoginNotificationEpoch';

function generateEpoch() {
    return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function readEpoch() {
    try {
        return localStorage.getItem(EPOCH_LS_KEY);
    } catch (e) {
        return null;
    }
}

function writeEpoch(epoch) {
    try {
        localStorage.setItem(EPOCH_LS_KEY, epoch);
    } catch (e) {
        // ignore
    }
}

function removeEpoch() {
    try {
        localStorage.removeItem(EPOCH_LS_KEY);
    } catch (e) {
        // ignore
    }
}

/**
 * useAfterLoginNotification
 *
 * Encapsulates the after-login notification popup logic:
 *  - Fetches /api/config/getDistNotificationData after the user is authenticated.
 *    The server resolves the current user's distributor group from the login session.
 *  - Respects a login-session-scoped dismissal flag stored in localStorage paired with
 *    a login-scoped epoch so the popup does not re-appear across tabs within
 *    the same login session, but does re-appear on a fresh login. Dismissal
 *    survives browser restarts unless an explicit logout clears localStorage.
 *  - Persists an optional permanent dismissal locally for the specific notification.
 *
 * Session tracking:
 *  - Uses Redux isAuthenticated state transitions (via useRef) to distinguish:
 *      null  → true  = page refresh / new tab (token still valid) → reuse existing epoch
 *      false → true  = explicit login (LOGIN_SUCCESS)              → generate new epoch
 *      true  → false = logout / auth failure                        → clear epoch + dismissal
 *  - Both epoch and dismissal are stored in localStorage for cross-tab sharing.
 *
 * Returns: { open, notification, handleClose }
 *   open        - whether the dialog should currently be open.
 *   handleClose - call with ({ hideFuture: boolean }) when the user dismisses
 *                 the dialog.
 */
export default function useAfterLoginNotification() {
    const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
    const [open, setOpen] = useState(false);
    const notificationIdRef = useRef(null);
    const [notification, setNotification] = useState(null);
    const prevAuthRef = useRef(isAuthenticated);
    const epochRef = useRef(null);

    // ---- Epoch management: detect isAuthenticated state transitions ----
    useEffect(() => {
        const prev = prevAuthRef.current;

        if (prev === false && isAuthenticated === true) {
            // Explicit login (LOGIN_SUCCESS): force a new epoch so that
            // any dismissal from a previous login session is invalidated.
            const epoch = generateEpoch();
            writeEpoch(epoch);
            epochRef.current = epoch;
        } else if (isAuthenticated === true) {
            // Page refresh or new tab (null → true) — token was still valid.
            // Reuse the existing epoch stored in localStorage; generate one
            // only if none exists yet (first-ever login in this browser).
            let epoch = readEpoch();
            if (!epoch) {
                epoch = generateEpoch();
                writeEpoch(epoch);
            }
            epochRef.current = epoch;
        } else if (isAuthenticated === false) {
            // Logout (LOGOUT) or auth failure (LOGIN_FAILURE):
            // clear both the epoch and the dismissal record so the next
            // explicit login starts fresh.
            removeEpoch();
            try {
                localStorage.removeItem(DISMISSAL_LS_KEY);
            } catch (e) {
                // ignore
            }
            epochRef.current = null;
            notificationIdRef.current = null;
            setNotification(null);
            setOpen(false);
        }
        // When isAuthenticated is null (initialising), do nothing.

        prevAuthRef.current = isAuthenticated;
    }, [isAuthenticated]);

    // ---- Popup status check ----
    useEffect(() => {
        if (!isAuthenticated) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const role = JSON.parse(localStorage.getItem('role'));
                const distrGroupId = JSON.parse(
                    localStorage.getItem(role === 'DR' ? 'doctor_dist_id' : 'distr_group_id')
                );
                const { data } = await axios.get('/config/getDistNotificationData', {
                    params: { distr_group_id: distrGroupId },
                });
                if (cancelled) return;
                if (!data || !data.success || !data.show || !data.notification) {
                    setNotification(null);
                    setOpen(false);
                    return;
                }

                const notificationId = String(data.notification.distr_notification_id);

                try {
                    if (localStorage.getItem(HIDE_FUTURE_LS_KEY) === notificationId) return;
                } catch (e) {
                    // ignore
                }

                // Read the cross-tab dismissal record from localStorage.
                let stored = null;
                try {
                    stored = JSON.parse(localStorage.getItem(DISMISSAL_LS_KEY));
                } catch (e) {
                    // ignore
                }

                // If the stored notification matches AND the epoch matches,
                // the user has already dismissed it during this login session.
                const currentEpoch = epochRef.current;
                if (
                    stored &&
                    stored.notificationId === notificationId &&
                    stored.epoch === currentEpoch
                ) {
                    return;
                }

                notificationIdRef.current = notificationId;
                setNotification(data.notification);
                setOpen(true);
            } catch (err) {
                // Non-blocking: if status check fails just don't show.
                // eslint-disable-next-line no-console
                console.warn('After-login notification status check failed', err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    const handleClose = useCallback(async ({ hideFuture } = {}) => {
        setOpen(false);
        const notificationId = notificationIdRef.current;
        if (!notificationId) return;

        if (hideFuture) {
            try {
                localStorage.setItem(HIDE_FUTURE_LS_KEY, notificationId);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('After-login notification dismissal persist failed', err);
            }
            return;
        }

        // Login-session-scoped dismissal: persist to localStorage paired with
        // the current login epoch so the dismissal is visible across all tabs
        // opened within the same login session (cleared on explicit logout).
        try {
            const payload = JSON.stringify({
                notificationId,
                epoch: epochRef.current,
            });
            localStorage.setItem(DISMISSAL_LS_KEY, payload);
        } catch (e) {
            // ignore
        }
    }, []);

    return { open, notification, handleClose };
}
